import {UserGroupIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';
import {toast} from 'react-toastify';

import {
  sendBulkPushNotification,
  sendSinglePushNotification,
} from '@/api/notification';
import {Button} from '@/components';
import Badge from '@/components/common/Badge';
import {Input, TextArea} from '@/components/common/Input';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexCountryCode} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {getErrorMessage} from '@/utils/errorMessage';

export default function PushNotificationForm() {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const countriesWithFlags = mapCountryCodeToFlags(countryList);
  const countryOptions = [
    {key: 'All Users', value: ''},
    ...Object.entries(countriesWithFlags).map(([code, flag]) => ({
      key: `${code} ${flag}`,
      value: code,
    })),
  ];

  const [selectedCountries, setSelectedCountries] = useState<
    AfriexCountryCode[]
  >([]);
  const [action, setAction] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [notificationDetails, setNotificationDetails] = useState({
    email: '',
    title: '',
    message: '',
  });

  const handleActionChange = ({target}: {target: HTMLInputElement}) => {
    setAction(target.value);
  };

  const toggleCountrySelection = (value: AfriexCountryCode | '') => {
    setSelectedCountries((prevSelected: AfriexCountryCode[]) => {
      if (value === '') {
        return ['' as AfriexCountryCode];
      }

      if (prevSelected.includes(value)) {
        return prevSelected.filter(
          (country: AfriexCountryCode) => country !== value,
        );
      }

      return prevSelected.includes('' as AfriexCountryCode)
        ? [value]
        : [...prevSelected, value];
    });
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNotificationDetails({
      ...notificationDetails,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setOpenModal(true);
  };

  const clearInputs = () => {
    setNotificationDetails({
      title: '',
      email: '',
      message: '',
    });
    setSelectedCountries([]);
  };

  const sendNotification = async () => {
    try {
      setLoading(true);
      if (action === 'masspush') {
        const response = await sendBulkPushNotification({
          message: notificationDetails?.message,
          title: notificationDetails?.title,
          countries: (selectedCountries.length
            ? selectedCountries.join(',')
            : '') as AfriexCountryCode,
        });
        if (response) {
          setLoading(false);
          setOpenModal(false);
          toast.success('Notification Sent');
          clearInputs();
        }
      } else {
        const response = await sendSinglePushNotification({
          email: notificationDetails?.email,
          message: notificationDetails?.message,
          title: notificationDetails?.title,
        });
        if (response) {
          setLoading(false);
          setOpenModal(false);
          toast.success('Notification Sent');
          clearInputs();
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Unknown Error. Contact Admin!'));
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-center mt-10 px-6">
        <div
          className={`flex flex-col ${action === 'masspush' ? 'w-full md:w-9/12' : 'w-full md:w-7/12'}`}
        >
          <div className="mb-4">
            <span className="block text-sm md:text-base mb-2 md:mb-0">
              Send To:
            </span>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <label className="flex items-center">
                <input
                  onChange={handleActionChange}
                  type="radio"
                  id="masspush"
                  value="masspush"
                  name="action"
                  checked={action === 'masspush'}
                  className="mr-2"
                />
                <span className="ml-1 text-sm md:text-base">Mass Push</span>
              </label>

              <label className="flex items-center">
                <input
                  onChange={handleActionChange}
                  type="radio"
                  id="individual"
                  value=""
                  name="action"
                  checked={action === ''}
                  className="mr-2"
                />
                <span className="ml-1 text-sm md:text-base">Individual</span>
              </label>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-transparent border-2 border-gray-200 dark:border-dark-borderMid rounded-md p-6"
          >
            <div className="flex flex-col gap-6">
              {/* Email */}
              {action !== 'masspush' && (
                <Input
                  label="Email"
                  type="email"
                  required
                  name="email"
                  value={notificationDetails.email}
                  onChange={handleInputChange}
                  className="w-full"
                  autoComplete="off"
                />
              )}

              {/* Country */}
              {action === 'masspush' && (
                <div>
                  <div>Select Country(s)</div>
                  <div>
                    {countryOptions.map(({key, value}) => (
                      <Badge
                        key={value}
                        className="mx-1 my-2 px-2"
                        colorScheme={
                          selectedCountries.includes(value as AfriexCountryCode)
                            ? 'indigo'
                            : ''
                        }
                      >
                        <button
                          className="hover:bg-indigo-200 inline-flex"
                          type="button"
                          onClick={() =>
                            toggleCountrySelection(value as AfriexCountryCode)
                          }
                        >
                          {key}
                          {value === '' && (
                            <UserGroupIcon className="w-4 h-4 text-indigo-700 ml-1" />
                          )}
                        </button>
                      </Badge>
                    ))}
                  </div>

                  {/* Display Selected Countries */}
                  {selectedCountries.length > 0 && (
                    <div className="mt-2 text-sm">
                      <strong>Selected:</strong> {selectedCountries.join(', ')}
                    </div>
                  )}
                </div>
              )}

              {/* Title */}
              <Input
                label="Title"
                type="text"
                required
                name="title"
                value={notificationDetails.title}
                onChange={handleInputChange}
                className="w-full"
                autoComplete="off"
              />

              {/* Message */}
              <TextArea
                label="Message"
                name="message"
                required
                onChange={handleInputChange}
                value={notificationDetails.message}
                aria-rowspan={4}
                type="text"
                className="w-full isFull"
                placeholder="Enter Message..."
              />

              <Button
                type="submit"
                colorScheme="cyan"
                variant="solid"
                className="h-10"
                disabled={
                  action === 'masspush' &&
                  selectedCountries.filter(Boolean).length === 0
                }
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationModal
        isOpen={openModal}
        label={
          <span className="font-bold text-center text-gray-900 dark:text-gray-200">
            Are you sure you want to send this Push Notification?
          </span>
        }
        cancelAction={() => setOpenModal(false)}
        cancelActionLabel="No"
        confirmAction={sendNotification}
        confirmActionLabel="Yes"
        loadingLabel="Sending..."
        loading={loading}
        title=""
        width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      >
        <div className="flex flex-col mx-5">
          <div className="pt-5 text-sm">
            <span className="font-semibold text-cyan-600">Title: </span>
            {notificationDetails.title}
          </div>
          <div className="pt-5 text-sm">
            <span className="font-semibold text-cyan-600">Message: </span>
            {notificationDetails.message}
          </div>
        </div>
      </ConfirmationModal>
    </>
  );
}
